import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanButton, StanCard, StanModal } from '@/components';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { EmailSubjectField } from '@/features/email-flows/components/EmailSubjectField';
import { EmailBodyField } from '@/features/email-flows/components/EmailBodyField';
import { useCourseEmailTemplate } from '../../queries/useCourseEmailTemplate';
import { useSendCourseMassEmail } from '@/queries/useCourses';
import { CourseEmailFormData, courseEmailFormSchema } from '../../validations/course';
import { CourseEmailEditorProps } from '../../types/courseAnalytics.types';
import { stanNotify } from '@/lib';
import { useModal } from '@/hooks/useModal';

export function CourseEmailEditor({
    courseId,
    filteredUsersCount,
    totalUsersCount,
    pendingFanIds,
    hasActiveFilters = false,
    onCancel,
    onEmailSent,
}: CourseEmailEditorProps) {
    // Confirmation modal
    const confirmModal = useModal();

    // Fetch email template variables
    const { data: templateData } = useCourseEmailTemplate();

    // Form setup
    const {
        control,
        formState: { errors, isSubmitting, isValid },
        handleSubmit,
        setValue,
        reset,
    } = useForm<CourseEmailFormData>({
        resolver: zodResolver(courseEmailFormSchema),
        mode: 'onChange', // Enable real-time validation
        defaultValues: {
            subject: '',
            body: '',
        },
    });

    // Set default values from template (reset with keepDirty: false to avoid dirty state)
    useEffect(() => {
        if (templateData?.default && (templateData.default.subject || templateData.default.body)) {
            reset(
                {
                    subject: templateData.default.subject,
                    body: templateData.default.body,
                },
                { keepDirty: false }
            );
        }
    }, [templateData, reset]);

    // Send email mutation
    const { mutateAsync: sendEmail, isPending } = useSendCourseMassEmail();

    // Handle form submission with useCallback for performance
    const onSubmit = useCallback(
        async (data: CourseEmailFormData) => {
            // Prevent submission if no recipients
            if (filteredUsersCount === 0) {
                return;
            }

            try {
                await sendEmail({
                    courseId,
                    payload: {
                        fans: pendingFanIds,
                        subject: data.subject,
                        body: data.body,
                    },
                });

                stanNotify({
                    type: 'success',
                    title: 'Email Sent',
                    text: `Email successfully sent to ${filteredUsersCount} user${filteredUsersCount !== 1 ? 's' : ''}`,
                    duration: 3000,
                });

                onEmailSent();

                // Reset form to template defaults after successful send
                if (templateData?.default) {
                    reset(templateData.default, { keepDirty: false });
                }
            } catch (error) {
                // Error notification
                const errorMessage =
                    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                    'Failed to send email. Please try again.';
                stanNotify({
                    type: 'error',
                    title: 'Send Failed',
                    text: errorMessage,
                    duration: 3000,
                });
            }
        },
        [filteredUsersCount, sendEmail, courseId, pendingFanIds, onEmailSent, reset, templateData]
    );

    // Handle cancel with form reset
    const handleCancel = useCallback(() => {
        onCancel();
        // Reset form to template defaults when canceling
        if (templateData?.default) {
            reset(templateData.default, { keepDirty: false });
        }
    }, [onCancel, reset, templateData]);

    const isLoading = isSubmitting || isPending;
    const hasRecipients = filteredUsersCount > 0;

    // Handle form submission - show confirmation modal
    const handleFormSubmit = handleSubmit(() => {
        confirmModal.open();
    });

    return (
        <DropdownProvider dropdownId='course-email-editor' dropdownName='course-email-editor'>
            <StanCard className='w-full !items-stretch'>
                <div className='mb-6 w-full'>
                    <div className='mb-4 flex w-full items-start justify-between'>
                        <div>
                            <h3 className='text-dark mb-2'>Send Email to Users</h3>
                            {hasRecipients ? (
                                <div className='space-y-1'>
                                    <p className='text-gray text-label-sm-regular'>
                                        {hasActiveFilters ? (
                                            <>
                                                Sending to{' '}
                                                <span className='text-dark font-semibold'>{filteredUsersCount}</span>{' '}
                                                filtered user{filteredUsersCount !== 1 ? 's' : ''} (out of{' '}
                                                {totalUsersCount} total enrolled)
                                            </>
                                        ) : (
                                            <>
                                                Sending to{' '}
                                                <span className='text-dark font-semibold'>{filteredUsersCount}</span>{' '}
                                                enrolled user{filteredUsersCount !== 1 ? 's' : ''}
                                            </>
                                        )}
                                    </p>
                                    {hasActiveFilters && (
                                        <p className='text-brand-main text-label-xs-regular'>
                                            Filters are active. Only users matching your filter criteria will receive
                                            this email.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <p className='text-warning-active text-label-sm-bold'>
                                    No recipients match your current filters. Adjust your filters to include users.
                                </p>
                            )}
                        </div>
                        <StanButton name='course-email-edit-button' color='primary' size='sm' onClick={handleCancel}>
                            Edit
                        </StanButton>
                    </div>
                </div>

                {hasRecipients ? (
                    <form onSubmit={handleFormSubmit} className='w-full space-y-6'>
                        <EmailSubjectField
                            control={control}
                            errors={errors}
                            disabled={isLoading}
                            templateVariables={templateData?.variables}
                        />

                        <EmailBodyField
                            control={control}
                            errors={errors}
                            setValue={setValue}
                            disabled={isLoading}
                            templateVariables={templateData?.variables}
                            templateDefaults={templateData?.default}
                        />

                        <div className='flex items-center justify-end gap-3 pt-4'>
                            <StanButton
                                name='course-email-cancel-button'
                                color='secondary'
                                size='md'
                                onClick={handleCancel}
                                disabled={isLoading}
                            >
                                Cancel
                            </StanButton>
                            <StanButton
                                name='course-email-send-button'
                                color='primary'
                                size='md'
                                type='submit'
                                disabled={isLoading || !isValid}
                                loading={isLoading}
                            >
                                Send Email
                            </StanButton>
                        </div>
                    </form>
                ) : (
                    <div className='flex items-center justify-end pt-4'>
                        <StanButton name='course-email-close-button' color='secondary' size='md' onClick={handleCancel}>
                            Close
                        </StanButton>
                    </div>
                )}
            </StanCard>

            {/* Confirmation Modal */}
            <StanModal
                id='confirm-send-email-modal'
                isOpen={confirmModal.isOpen}
                onClose={confirmModal.close}
                title='Just confirming!'
                size='sm'
                primaryActionLabel='Confirm Send'
                secondaryActionLabel='Cancel'
                onPrimaryAction={() => {
                    confirmModal.close();
                    handleSubmit(onSubmit)();
                }}
                onSecondaryAction={confirmModal.close}
                primaryActionLoading={isLoading}
                primaryActionDisabled={isLoading}
            >
                <p className='text-gray text-label-sm-regular text-center'>
                    Are you sure you want to send this email to{' '}
                    <span className='text-dark font-semibold'>{filteredUsersCount}</span>{' '}
                    {filteredUsersCount === 1 ? 'fan' : 'fans'}?
                </p>
            </StanModal>
        </DropdownProvider>
    );
}
